import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Bilingue } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import type { Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

/**
 * Actualités — `/v1/g/:slug/actualites` (`docs/dev/api.md` § 15, 4.1 `posts`).
 * Client volontairement léger (S3-d, volet écran) : l'écran des expositions
 * n'ouvre que ce qu'il faut pour lister et créer une actualité rattachée à
 * une exposition (`expositionId`, § 15 « actualités.routes.ts »), pas un
 * module « actualités » complet — hors périmètre de ce ticket.
 */
export interface ActualitePresentee {
  readonly id: string;
  readonly titre: Bilingue;
  readonly slug: string;
  readonly slugPublie: string | null;
  readonly chapo: Bilingue | null;
  readonly texte: Bilingue;
  readonly date: string;
  readonly artistes: readonly string[];
  readonly expositions: readonly string[];
  readonly visibleSurLeSite: boolean;
  readonly archive: boolean;
  readonly creeLe: string;
  readonly modifieLe: string;
}

export interface CorpsActualite {
  titre: { fr: string; en?: string };
  texte: { fr: string; en?: string };
  date?: string;
  expositions?: readonly string[];
  visibleSurLeSite?: boolean;
}

interface ReponseListeActualites {
  actualites: readonly ActualitePresentee[];
  pagination: Pagination;
}

const cleListeParExposition = (galerie: string, expositionId: string) =>
  cleMetier(galerie, 'actualites', 'exposition', expositionId);

export const actualitesApi = {
  listerParExposition: (galerie: string, expositionId: string, appel?: AppelApi) =>
    api.get<ReponseListeActualites>(
      `/g/${encodeURIComponent(galerie)}/actualites?expositionId=${encodeURIComponent(expositionId)}&parPage=100`,
      { galerie, ...appel },
    ),
  creer: (galerie: string, corps: CorpsActualite, appel?: AppelApi) =>
    api.post<{ actualite: ActualitePresentee }>(
      `/g/${encodeURIComponent(galerie)}/actualites`,
      corps,
      { galerie, ...appel },
    ),
};

export function useActualitesDeLExposition(galerie: string, expositionId: string | null) {
  return useQuery({
    queryKey: cleListeParExposition(galerie, expositionId ?? ''),
    queryFn: (contexte) =>
      actualitesApi.listerParExposition(galerie, expositionId ?? '', appelDeRequete(contexte)),
    select: (reponse) => reponse.actualites,
    enabled: expositionId !== null,
  });
}

export function useCreerActualite(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsActualite, appel) => actualitesApi.creer(galerie, corps, appel),
      onSuccess: (_donnees, variables) => {
        for (const expositionId of variables.expositions ?? []) {
          void client.invalidateQueries({
            queryKey: cleListeParExposition(galerie, expositionId),
          });
        }
      },
    }),
  );
}
